import React, { useEffect } from 'react';

import { yupResolver } from '@hookform/resolvers/yup';
import {
  useGetBonus25Methods,
  useGetBonusTypes,
  useGetCLAOptions
} from 'queries';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useQueryClient } from 'react-query';

import { PremiumPayFlagsEnum } from '../../types';
import { PremiumPayFormValues } from './types';

import { isSvcTypeBonus, mapToOptions } from '../../utils/utils';
import { convertPayloadForSave } from './utils/utils';

import { PREMIUM_PAYS } from 'RestAPIs/endpoints';
import { useSaveResource } from 'RestAPIs/useSaveResource';

import { LoadingButton } from '@mui/lab';
import { Box } from '@mui/material';
import Grid from '@mui/material/Grid';

import {
  ControlledAutoComplete,
  ControlledCheckbox,
  ControlledDatePicker,
  ControlledSelect,
  ControlledTimeFromTimeTo,
  ControlledTimePicker
} from 'components/shared';
import TextField from 'components/shared/ControlledFields/ControlledTextField';
import MultiStepForm from 'components/shared/MultiStepForm/MultiStepForm';

import { schema } from './schema';
import { GridCompact6 } from 'components/shared/Grid/Grid.components';
import dayjs from 'dayjs';
import { TIME_RANGE_BASE_DATE } from 'utils/date';

type CreateEditFunctionFormProps = {
  handleClose: () => void;
  rowData?: PremiumPayFormValues;
};

const PremiumPayForm: React.FC<CreateEditFunctionFormProps> = ({
  handleClose,
  rowData
}) => {
  const { t } = useTranslation(['common']);
  const queryClient = useQueryClient();

  const { data: premiumPayTypes } = useGetCLAOptions();
  const { data: bonusTypes } = useGetBonusTypes();
  const { data: bonus25Methods } = useGetBonus25Methods();

  const { mutateAsync, isLoading } = useSaveResource({
    url: PREMIUM_PAYS,
    id: rowData?.claBonusId,
    invalidateQueryKey: ['premiumPays']
  });

  const methods = useForm<PremiumPayFormValues>({
    defaultValues: rowData
      ? {
          ...rowData,
          bonusTypeSvc: undefined,
          valid: {
            from: rowData.valid?.from || undefined,
            to: rowData.valid?.to || undefined
          }
        }
      : {},
    resolver: yupResolver(schema)
  });

  const { control, handleSubmit } = methods;

  const onSubmit = (data) => {
    const payload = convertPayloadForSave(data, premiumPayTypes);

    mutateAsync(payload)
      .then(async () => {
        await queryClient.invalidateQueries(['premiumPays', data?.claBonusId]);
      })
      .then(handleClose);
  };

  useEffect(() => {
    if (rowData) {
      methods.reset({
        ...rowData,
        bonusTypeSvc: mapToOptions(bonusTypes, 'code', 'name').find(
          ({ value }) => value === rowData.bonusTypeSvc?.label
        ),
        valid: {
          from: dayjs(rowData.valid?.from).toDate(),
          to: dayjs(rowData.valid?.to).toDate()
        }
      });
    }
  }, [rowData, bonusTypes]);

  return (
    <MultiStepForm onSubmit={handleSubmit(onSubmit)} methods={methods}>
      <MultiStepForm.Section
        title={t('premiumPays.form.sections.base')}
        validateFields={['claCode', 'bonusTypeSvc', 'columnNr', 'percentage']}
      >
        <ControlledSelect
          name="claCode"
          control={control}
          variant="outlined"
          label={t('premiumPays.grid.claId')}
          options={mapToOptions(premiumPayTypes, 'code', 'code')}
        />

        <ControlledAutoComplete
          name="bonusTypeSvc"
          control={control}
          variant="outlined"
          label={t('premiumPays.grid.bonusTypeSvc')}
          options={mapToOptions(bonusTypes, 'code', 'name')}
          idProperty="value"
          optionLabelProp="label"
          fullWidth
        />

        <Box display="flex" gap={2.5}>
          <TextField
            name="columnNr"
            type="number"
            control={control}
            variant="outlined"
            label={t('premiumPays.grid.columnNr')}
          />
          <TextField
            name="percentage"
            type="number"
            control={control}
            variant="outlined"
            label={t('premiumPays.grid.percentage')}
            inputProps={{
              step: 0.001
            }}
          />
        </Box>

        <ControlledTimeFromTimeTo
          control={control}
          variant="outlined"
          timeFromLabel={t('premiumPays.grid.timeFrom')}
          timeToLabel={t('premiumPays.grid.timeTo')}
          baseDate={TIME_RANGE_BASE_DATE}
        />

        {isSvcTypeBonus(methods.getValues(['bonusTypeSvc'])?.[0]?.value) && (
          <>
            <Box gap={2.5}>
              <ControlledSelect
                name="bonus25Method"
                control={control}
                variant="outlined"
                label={t('premiumPays.grid.bonus25Method')}
                options={mapToOptions(bonus25Methods, 'code', 'name')}
              />
            </Box>
            <Box display="flex" gap={2.5}>
              <TextField
                name="maxHoursPerDayrange"
                type="number"
                control={control}
                variant="outlined"
                label={t('premiumPays.grid.maxHoursPerDayrange')}
              />
              <TextField
                name="dayrange"
                type="number"
                control={control}
                variant="outlined"
                label={t('premiumPays.grid.dayrange')}
              />
            </Box>
          </>
        )}
      </MultiStepForm.Section>

      <MultiStepForm.Section
        title={t('premiumPays.grid.scAll')}
        validateFields={['scPri']}
      >
        <Box display="flex" gap={2.5}>
          <TextField
            name="scPri"
            type="text"
            control={control}
            variant="outlined"
            label={t('premiumPays.grid.scPri')}
          />
          <TextField
            name="scSec"
            type="text"
            control={control}
            variant="outlined"
            label={t('premiumPays.grid.scSec')}
          />
        </Box>
      </MultiStepForm.Section>

      <MultiStepForm.Section title={t('premiumPays.form.sections.days')}>
        <Grid container>
          <GridCompact6>
            <ControlledCheckbox
              control={control}
              name={`flags[${PremiumPayFlagsEnum.DAY_MONDAY}]`}
              label={t('weekday.monday')}
            />
          </GridCompact6>
          <GridCompact6>
            <ControlledCheckbox
              control={control}
              name={`flags[${PremiumPayFlagsEnum.DAY_TUESDAY}]`}
              label={t('weekday.tuesday')}
            />
          </GridCompact6>
          <GridCompact6>
            <ControlledCheckbox
              control={control}
              name={`flags[${PremiumPayFlagsEnum.DAY_WEDNESDAY}]`}
              label={t('weekday.wednesday')}
            />
          </GridCompact6>
          <GridCompact6>
            <ControlledCheckbox
              control={control}
              name={`flags[${PremiumPayFlagsEnum.DAY_THURSDAY}]`}
              label={t('weekday.thursday')}
            />
          </GridCompact6>
          <GridCompact6>
            <ControlledCheckbox
              control={control}
              name={`flags[${PremiumPayFlagsEnum.DAY_FRIDAY}]`}
              label={t('weekday.friday')}
            />
          </GridCompact6>
          <GridCompact6>
            <ControlledCheckbox
              control={control}
              name={`flags[${PremiumPayFlagsEnum.DAY_SATURDAY}]`}
              label={t('weekday.saturday')}
            />
          </GridCompact6>
          <GridCompact6>
            <ControlledCheckbox
              control={control}
              name={`flags[${PremiumPayFlagsEnum.DAY_SUNDAY}]`}
              label={t('weekday.sunday')}
            />
          </GridCompact6>
        </Grid>
      </MultiStepForm.Section>

      <MultiStepForm.Section title={t('premiumPays.form.sections.flags')}>
        <Grid container>
          <GridCompact6>
            <ControlledCheckbox
              control={control}
              name={`flags[${PremiumPayFlagsEnum.DAY_HOLIDAY}]`}
              label={t('premiumPays.grid.flags.DAY_HOLIDAY')}
            />
          </GridCompact6>

          <GridCompact6>
            <ControlledCheckbox
              control={control}
              name={`flags[${PremiumPayFlagsEnum.DAY_HOLIDAY_SAT}]`}
              label={t('premiumPays.grid.flags.DAY_HOLIDAY_SAT')}
            />
          </GridCompact6>
          <GridCompact6>
            <ControlledCheckbox
              control={control}
              name={`flags[${PremiumPayFlagsEnum.DAY_HOLIDAY_SUN}]`}
              label={t('premiumPays.grid.flags.DAY_HOLIDAY_SUN')}
            />
          </GridCompact6>

          <GridCompact6>
            <ControlledCheckbox
              control={control}
              name={`flags[${PremiumPayFlagsEnum.DAY_SPECIAL}]`}
              label={t('premiumPays.grid.flags.DAY_SPECIAL')}
            />
          </GridCompact6>
          <GridCompact6>
            <ControlledCheckbox
              control={control}
              name={`flags[${PremiumPayFlagsEnum.TIME_FROM_THR}]`}
              label={t('premiumPays.grid.flags.TIME_FROM_THR')}
            />
          </GridCompact6>
          <GridCompact6>
            <ControlledCheckbox
              control={control}
              name={`flags[${PremiumPayFlagsEnum.TIME_TO_THR}]`}
              label={t('premiumPays.grid.flags.TIME_TO_THR')}
            />
          </GridCompact6>
        </Grid>
        <Box display="flex" gap={2.5}>
          {methods.getValues(['flags'])?.[0]?.['TIME_FROM_THR'] && (
            <GridCompact6>
              <ControlledTimePicker
                name="timeFromThr"
                control={control}
                variant="outlined"
                label={t('premiumPays.grid.timeFromThr')}
                baseDate={TIME_RANGE_BASE_DATE}
              />
            </GridCompact6>
          )}

          {methods.getValues(['flags'])?.[0]?.['TIME_TO_THR'] && (
            <GridCompact6>
              <ControlledTimePicker
                name="timeToThr"
                control={control}
                variant="outlined"
                label={t('premiumPays.grid.timeToThr')}
                baseDate={TIME_RANGE_BASE_DATE}
              />
            </GridCompact6>
          )}
        </Box>
      </MultiStepForm.Section>

      <MultiStepForm.Section
        title={t('premiumPays.form.sections.valid')}
        validateFields={['valid.from', 'valid.to']}
      >
        <Box display="flex" gap={2.5}>
          <ControlledDatePicker
            control={control}
            name="valid.from"
            variant="outlined"
            label={t('validFrom')}
            bypassRef
          />
          <ControlledDatePicker
            control={control}
            name="valid.to"
            variant="outlined"
            label={t('validTo')}
            bypassRef
          />
        </Box>
      </MultiStepForm.Section>
      <MultiStepForm.Actions>
        <Box display="flex" ml="auto">
          <LoadingButton
            loading={isLoading}
            type="submit"
            variant="contained"
            autoFocus
            color="primary"
          >
            {t('save')}
          </LoadingButton>
        </Box>
      </MultiStepForm.Actions>
    </MultiStepForm>
  );
};

export default PremiumPayForm;
